import React, { useState, useEffect, useCallback } from 'react';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import excelExportIcon from '../images/excelexport.png';
import { useWebSocket } from './WebSocketContext';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';

const ExportToExcel = ({ data, fileName, caseid }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState({ open: false, message: '', severity: 'info' });
  
  const primaryColor = '#5757b0';
  const lightPurple = '#f0f0ff';
  // Function to handle WebSocket messages related to export
  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      // Message is already parsed in WebSocketContext
      // Only handle regular exportexcel command, not variable exports
      if (parsedMessage.command === 'exportexcel') {
        setIsExporting(false);
        
        if (parsedMessage.data && parsedMessage.data.status === 'success' && parsedMessage.data.data) {
          // Handle successful export with base64 data
          try {
            // Prevent duplicate downloads by checking if this file was already downloaded
            const downloadId = `${parsedMessage.data.filename}_${parsedMessage.data.count}`;
            const downloadKey = `excel_download_${downloadId}`;
            
            // Check if we already downloaded this file in the last 5 seconds
            const lastDownload = sessionStorage.getItem(downloadKey);
            const now = Date.now();
            if (lastDownload && (now - parseInt(lastDownload)) < 5000) {
              console.log('Preventing duplicate download for:', downloadId);
              return;
            }
            
            // Mark this download as processed
            sessionStorage.setItem(downloadKey, now.toString());
            
            // Convert base64 to blob
            const binaryString = atob(parsedMessage.data.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { 
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            // Create download link and trigger download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = parsedMessage.data.filename || 'export.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            setExportStatus({
              open: true,
              message: t('export_to_excel.success', `Export successful (${parsedMessage.data.count || 0} records)`),
              severity: 'success'
            });
          } catch (error) {
            console.error('Error processing Excel download:', error);
            setExportStatus({
              open: true,
              message: t('export_to_excel.download_error', 'Failed to download the Excel file'),
              severity: 'error'
            });
          }
        } else if (parsedMessage.data && parsedMessage.data.status === 'success') {
          // Legacy success message (should not happen with new implementation)
          setExportStatus({
            open: true,
            message: t('export_to_excel.success', 'Export successful'),
            severity: 'success'
          });
        } else if (parsedMessage.data && parsedMessage.data.status === 'error') {
          setExportStatus({
            open: true,
            message: parsedMessage.data.message || t('export_to_excel.error', 'Export failed'),
            severity: 'error'
          });
        }
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, t]);

  // Handle closing the status alert
  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setExportStatus(prev => ({ ...prev, open: false }));
  };

  // Function to export data to Excel
  const exportToExcel = useCallback(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error('Invalid data');
      return;
    }

    setIsExporting(true);
    ws.send(JSON.stringify({ command: 'exportexcel', caseid: caseid }));
  }, [data, ws, caseid]);

  return (
    <>
      <Tooltip title={t('export_to_excel.tooltip')}>
        <IconButton 
          onClick={exportToExcel} 
          disabled={isExporting}
          sx={{
            position: 'relative',
            '&.Mui-disabled': {
              opacity: 0.7,
              filter: 'grayscale(30%)'
            }
          }}
        >
          <img 
            src={excelExportIcon} 
            alt={t('export_to_excel.alt_text')} 
            style={{ width: 24, height: 24 }} 
          />
          {isExporting && (
            <CircularProgress 
              size={28} 
              sx={{ 
                position: 'absolute', 
                color: primaryColor,
                opacity: 0.8
              }} 
            />
          )}
        </IconButton>
      </Tooltip>
      
      {/* Loading indicator shown at the bottom of the screen */}
      <Snackbar 
        open={isExporting} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Slide}
        sx={{ bottom: exportStatus.open ? '80px' : '20px' }}
      >
        <Alert 
          icon={<CircularProgress size={20} sx={{ color: primaryColor }} />}
          severity="info" 
          sx={{ 
            width: '100%', 
            backgroundColor: lightPurple, 
            color: primaryColor,
            border: `1px solid ${primaryColor}`,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.15)',
            display: 'flex',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              color: primaryColor,
              marginRight: '12px',
              padding: '0'
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {t('export_to_excel.exporting', 'Exporting data to Excel...')}
          </Typography>
        </Alert>
      </Snackbar>
      
      {/* Success/Error notification */}
      <Snackbar 
        open={exportStatus.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={exportStatus.severity} 
          sx={{ 
            width: '100%',
            backgroundColor: exportStatus.severity === 'success' ? '#e8f5e9' : (exportStatus.severity === 'error' ? '#ffebee' : lightPurple),
            color: exportStatus.severity === 'success' ? '#2e7d32' : (exportStatus.severity === 'error' ? '#d32f2f' : primaryColor),
            border: `1px solid ${exportStatus.severity === 'success' ? '#a5d6a7' : (exportStatus.severity === 'error' ? '#ef9a9a' : primaryColor)}`,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '& .MuiAlert-icon': {
              color: exportStatus.severity === 'success' ? '#2e7d32' : (exportStatus.severity === 'error' ? '#d32f2f' : primaryColor)
            }
          }}
        >
          {exportStatus.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportToExcel;
